import {
  ArrowClockwiseIcon,
  DownloadSimpleIcon,
  FileArchiveIcon,
  PlusSquareIcon,
  SpinnerIcon,
  TrashIcon,
  WarningDiamondIcon,
} from '@phosphor-icons/react';
import { filesize } from 'filesize';
import truncate from 'lodash/truncate';
import { ReactElement, useEffect, useState } from 'react';
import { Control, FieldValues, Path, useFormContext } from 'react-hook-form';

import CameraIcon from '@/assets/svgs/Camera';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { prefetchFileFromUrl } from '@/helpers/s3-file-manager';
import { cn } from '@/lib/utils';

export type FilePickerProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  accept: string[];
  error?: string;
  maxFileSize?: number;
  tips?: ReactElement;
  placeholder?: string;
  isFileSizeEmpty?: boolean;
  withFilePreview?: boolean;
  useVideoIcon?: boolean;
  requiredMark?: boolean;
  needPrefetch?: boolean;
  initialFileName?: string;
  initialFileSize?: number;
};

export const FilePicker = <T extends FieldValues>({
  name,
  label,
  tips,
  accept,
  error,
  maxFileSize,
  isFileSizeEmpty,
  useVideoIcon = false,
  withFilePreview = false,
  placeholder = 'Upload file',
  requiredMark,
  needPrefetch = false,
  initialFileName,
  initialFileSize,
}: FilePickerProps<T>) => {
  const { control, setValue, setError, getValues } = useFormContext();
  const [filePreviewFile, setFilePreviewFile] = useState<File | null>(null);
  const [isPrefetching, setIsPrefetching] = useState(false);

  const formFile = getValues(name);

  const getFileSizeValidationErrors = (file: File) => {
    return maxFileSize > 0 && file.size > maxFileSize
      ? `The uploaded file is too large. Maximum allowed size is ${filesize(maxFileSize, { precision: 1 })}`
      : '';
  };

  const pickFile = (event) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];
    const validationError = getFileSizeValidationErrors(file);

    if (!validationError) {
      setValue(name, file, { shouldValidate: true, shouldDirty: true });
      const url = URL.createObjectURL(file);
      setFilePreviewFile(file);
    } else {
      setError(name, {
        message: validationError,
      });
    }
  };

  const clearFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsPrefetching(false);
    setFilePreviewFile(null);
    setValue(name as string, '', { shouldValidate: true, shouldDirty: true });
  };

  const downloadFile = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const value = getValues(name);
    if (!value) return;

    const isFile = value instanceof File;
    const link = document.createElement('a');
    link.href = isFile ? URL.createObjectURL(value) : value;
    link.download = isFile ? value.name : initialFileName || '';

    document.body.appendChild(link);
    link.click();
    link.remove();

    if (isFile) {
      URL.revokeObjectURL(link.href);
    }
  };

  useEffect(() => {
    if (needPrefetch && formFile && typeof formFile === 'string') {
      const prefetchFile = async (url: string) => {
        try {
          setIsPrefetching(true);
          const prefetchedFile = await prefetchFileFromUrl(url);

          setFilePreviewFile(prefetchedFile);
        } catch (error) {
          console.error('Error prefetching file:', error);
        } finally {
          setIsPrefetching(false);
        }
      };

      prefetchFile(formFile);
    }
  }, [formFile]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(filePreviewFile as unknown as string);
      setFilePreviewFile(null);
    };
  }, []);

  const fileIcon = useVideoIcon ? (
    <CameraIcon />
  ) : (
    <FileArchiveIcon fill="white" size={36} />
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          {label && (
            <FormLabel
              htmlFor={`dropzone-file-${name}`}
              className="leading-[17px]"
            >
              {label}
              {requiredMark && <span className="text-red-500">*</span>}
            </FormLabel>
          )}

          <FormControl>
            <label
              htmlFor={`dropzone-file-${name}`}
              className={cn(
                'items-center justify-center mt-1 flex flex-col p-[20px] w-full border-2 border-zinc-700 border-dashed rounded-lg cursor-pointer bg-zinc-900',
                field.value && 'items-start',
                error && 'border-destructive border-solid'
              )}
            >
              {field.value ? (
                <>
                  <div className="flex items-center justify-between gap-2.5 w-full">
                    <div className="flex flex-1 gap-3">
                      {withFilePreview && !error ? (
                        <div className="rounded-md flex items-center justify-center overflow-hidden">
                          {isPrefetching ? (
                            <SpinnerIcon size={32} className="animate-spin" />
                          ) : (
                            <img
                              src={
                                filePreviewFile
                                  ? URL.createObjectURL(filePreviewFile)
                                  : field.value
                              }
                              alt="File Preview"
                              className="w-auto h-auto max-h-[70px] object-cover border border-zinc-200"
                            />
                          )}
                        </div>
                      ) : (
                        <div className="size-12 bg-zinc-800 rounded-md flex items-center justify-center">
                          {error ? (
                            <WarningDiamondIcon
                              color="hsl(var(--destructive))"
                              size={28}
                            />
                          ) : isPrefetching ? (
                            <SpinnerIcon size={32} className="animate-spin" />
                          ) : (
                            fileIcon
                          )}
                        </div>
                      )}
                      <div className="flex flex-col gap-1 flex-1">
                        <p className="text-white line-clamp-1 flex-1">
                          {truncate(
                            typeof field.value === 'object' && field.value
                              ? field.value.name
                              : initialFileName || field.value,
                            { length: 50 }
                          )}
                        </p>
                        {isPrefetching ? (
                          <p className="text-zinc-400 text-sm">
                            calculating size...
                          </p>
                        ) : (
                          <p className="text-zinc-400 text-sm">
                            {(() => {
                              const size =
                                filePreviewFile?.size ||
                                (typeof field.value === 'string'
                                  ? initialFileSize
                                  : 0);

                              return size && size > 0 && !isFileSizeEmpty
                                ? filesize(size)
                                : 'Unknown size';
                            })()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!error && (
                        <DownloadSimpleIcon
                          data-testid="download-file-button"
                          className="cursor-pointer"
                          size={32}
                          onClick={downloadFile}
                        />
                      )}
                      <TrashIcon
                        data-testid="remove-file-button"
                        className="cursor-pointer"
                        size={32}
                        onClick={clearFile}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-2.5">
                    <div className="size-12 bg-zinc-800 rounded-md flex items-center justify-center">
                      <PlusSquareIcon fill="white" size={36} />
                    </div>
                    <p className="text-white">{placeholder}</p>
                  </div>
                  <input
                    id={`dropzone-file-${name}`}
                    type="file"
                    className="hidden"
                    onChange={pickFile}
                    accept={accept.join(',')}
                  />
                </>
              )}
            </label>
          </FormControl>

          <FormMessage errorMessage={error} />

          {tips}
        </FormItem>
      )}
    />
  );
};
